import { Box, SxProps } from '@mui/system';
import { GridCellParams, GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';

import {
  DatasetDeliveryTableDetails,
  DeliveryStatusEnum,
  usePatchDatasetDeliveryMutation,
  VerificationStatusEnum,
} from '@verantos-dev/data-access-dataset-delivery';
import {
  //  Button,
  ActionMenu,
  ActionMenuItemProps,
  //  StatusDisplay,
} from '@verantos-dev/shared-components';
import { stringCellStyles } from '@verantos-dev/shared-styles';

import { DatasetDialogType, useDatasetModalContext } from '../context/DatasetManifestDialogContext';

const headerEntryStyles: SxProps = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: 'normal',
  padding: '16px 24px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#252A36',
};

const RenderHeader = (params: GridColumnHeaderParams<DatasetDeliveryTableDetails>) => (
  <Box data-testid={`dataset-delivery-header-${params.colDef.field}`} sx={headerEntryStyles}>
    {params.colDef.headerName}
  </Box>
);

export const useDatasetDeliveryTable = () => {
  const intl = useIntl();
  const { openDialog } = useDatasetModalContext();
  const [mutate] = usePatchDatasetDeliveryMutation();

  const handleOnVerify = (dataset: string) => {
    mutate({
      dataset,
      data: {
        verification_status: 'ready_to_verify',
      },
    });
  };

  const handleOnDeliver = (dataset: string) => {
    mutate({
      dataset,
      data: {
        delivery_status: 'ready_to_deliver',
      },
    });
  };

  const columns: GridColDef<DatasetDeliveryTableDetails>[] = [
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery.tableHeader.organization' }),
      field: 'organization',
      type: 'string',
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams<DatasetDeliveryTableDetails>) => {
        const organization = params?.row.customers;
        return (
          <Box data-testid={`dataset-delivery-organization-${organization}`} sx={stringCellStyles}>
            {organization}
          </Box>
        );
      },
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery.tableHeader.dataset' }),
      field: 'dataset',
      type: 'string',
      minWidth: 500,
      maxWidth: 600,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams<DatasetDeliveryTableDetails>) => {
        const dataset = params?.row.dataset;
        return (
          <Box data-testid={`dataset-delivery-dataset-${dataset}`} sx={stringCellStyles}>
            {dataset}
          </Box>
        );
      },
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery.tableHeader.verificationStatus' }),
      field: 'verificationStatus',
      type: 'string',
      minWidth: 130,
      flex: 1,
      filterable: true,
      sortable: true,
      renderCell: (params: GridCellParams<DatasetDeliveryTableDetails>) => {
        const verificationStatus = params?.row.verification_status;
        let verificationMessage = params?.row.verification_message;
        if (verificationStatus === 'verify_failed') {
          const jsonObject = JSON.parse(params?.row.verification_message);
          verificationMessage = 'Verification failed:';
          for (const test in jsonObject) {
            if (jsonObject[test] === 'fail') {
              verificationMessage = verificationMessage + ' ' + test;
            }
          }
        } else if (verificationStatus === 'verified') {
          verificationMessage = 'Verified';
        }
        return (
          <Box
            data-testid={`dataset-delivery-verificationStatus-${verificationStatus}`}
            sx={stringCellStyles}
          >
            {verificationMessage}
          </Box>
        );
      },
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery.tableHeader.deliveryStatus' }),
      field: 'deliveryStatus',
      type: 'string',
      minWidth: 130,
      flex: 1,
      filterable: true,
      sortable: true,
      renderCell: (params: GridCellParams<DatasetDeliveryTableDetails>) => {
        const deliveryStatus = params?.row.delivery_status;
        let deliveryMessage = params?.row.delivery_message;
        if (deliveryStatus === 'delivery_failed') {
          const jsonObject = JSON.parse(params?.row.delivery_message);
          deliveryMessage = 'Delivery failed:';
          for (const deliveryStep in jsonObject) {
            if (jsonObject[deliveryStep] === 'fail') {
              deliveryMessage = deliveryMessage + ' ' + deliveryStep;
            }
          }
        } else if (deliveryStatus === 'delivered') {
          deliveryMessage = 'Delivered';
        }
        return (
          <Box
            data-testid={`dataset-delivery-deliveryStatus-${deliveryStatus}`}
            sx={stringCellStyles}
          >
            {deliveryMessage}
          </Box>
        );
      },
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({
        id: 'common.grid.columnName.actions',
        defaultMessage: 'Actions',
      }),
      field: '',
      filterable: false,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      flex: 0,
      width: 80,
      type: 'actions',

      renderCell: (param) => {
        const actionMenu: ActionMenuItemProps[] = [
          {
            id: 'verifyDataset',
            label: 'Verify dataset',
            disabled: Boolean(
              param.row.verification_status &&
                [
                  VerificationStatusEnum.READY_TO_VERIFY,
                  VerificationStatusEnum.VERIFICATION_IN_PROGRESS,
                  VerificationStatusEnum.VERIFIED,
                ].includes(param?.row.verification_status as VerificationStatusEnum)
            ),
            onClick: () => {
              handleOnVerify(param.row.dataset);
            },
            icon: 'edit',
          },
          {
            id: 'editUser', //TODO: change id
            label: 'Deliver dataset',
            disabled: Boolean(
              (param?.row.delivery_status &&
                [
                  DeliveryStatusEnum.READY_TO_DELIVER,
                  DeliveryStatusEnum.DELIVERY_IN_PROGRESS,
                  DeliveryStatusEnum.DELIVERED,
                ].includes(param?.row.delivery_status as DeliveryStatusEnum)) ||
                [
                  VerificationStatusEnum.READY_TO_VERIFY,
                  VerificationStatusEnum.VERIFICATION_IN_PROGRESS,
                  VerificationStatusEnum.NOT_VERIFIED,
                  VerificationStatusEnum.VERIFY_FAILED,
                ].includes(param?.row.verification_status as VerificationStatusEnum)
            ),
            onClick: () => {
              handleOnDeliver(param.row.dataset);
            },
            icon: 'edit',
          },
          {
            id: 'datasetManifest',
            label: 'View detail',
            disabled: Boolean(
              param?.row.delivery_status &&
                [
                  DeliveryStatusEnum.NOT_DELIVERED,
                  DeliveryStatusEnum.READY_TO_DELIVER,
                  DeliveryStatusEnum.DELIVERY_IN_PROGRESS,
                ].includes(param?.row.delivery_status as DeliveryStatusEnum)
            ),
            onClick: () => {
              openDialog(DatasetDialogType.VIEW_DATASET_DETAIL, param.row.dataset);
            },
            icon: 'description',
          },
        ];

        return <ActionMenu id={'userList-actionMenu'} menuItems={actionMenu} />;
      },
      renderHeader: () => null,
    },
  ];

  return {
    columns,
  };
};
